import { Autocomplete, TextField } from "@mui/material";
import classNames from "classnames";
import lodash from "lodash";
import * as React from "react";

import {
  ThreeDPanelState,
  usePanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { setThreeDPanelConfig } from "@/shared/state/visualization/actions";
import { ThreeDPanelConfig } from "@/shared/state/visualization/schema";
import { DEFAULT_ENTITY_ID } from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./SceneSettings.module.css";

interface CameraSettingsProps {
  panelId: string;
}

export function CameraSettings({ panelId }: CameraSettingsProps) {
  const dispatch = useVizDispatch();
  const panelState = usePanelState(panelId) as ThreeDPanelState;
  const camera = React.useMemo(
    () =>
      panelState.config?.camera ?? {
        type: "orbit",
        target: { id: DEFAULT_ENTITY_ID, mesh: "mesh" } as const,
        zoom: { min: 1, max: 50 },
      },
    [panelState.config?.camera],
  );
  const entities = panelState.data.entities;

  const entityIds = Object.keys(entities);

  const [target, setTarget] = React.useState(camera.target);
  const [minZoom, setMinZoom] = React.useState(`${camera.zoom?.min ?? ""}`);
  const [maxZoom, setMaxZoom] = React.useState(`${camera.zoom?.max ?? ""}`);

  const [targetError, setTargetError] = React.useState("");
  const [minZoomError, setMinZoomError] = React.useState("");
  const [maxZoomError, setMaxZoomError] = React.useState("");

  React.useEffect(
    function onSettingChanged() {
      setTargetError("");
      if (!entities[target.id] || !entities[target.id][target.mesh]) {
        setTargetError(`Component ${target.id}.${target.mesh} does not exist`);
        return;
      }
      setMinZoomError("");
      const parsedMinZoom =
        minZoom === "" ? undefined : Number.parseFloat(minZoom);
      if (parsedMinZoom === undefined) {
        // noop
      } else if (!Number.isFinite(parsedMinZoom)) {
        setMinZoomError("Zoom limits must be numbers");
        return;
      } else if (parsedMinZoom < 0) {
        setMinZoomError("Zoom limits must not be negative");
        return;
      }
      setMaxZoomError("");
      const parsedMaxZoom =
        maxZoom === "" ? undefined : Number.parseFloat(maxZoom);
      if (parsedMaxZoom === undefined) {
        // Noop
      } else if (!Number.isFinite(parsedMaxZoom)) {
        setMaxZoomError("Zoom limits must be numbers");
        return;
      } else if (parsedMaxZoom < 0) {
        setMaxZoomError("Zoom limits must not be negative");
        return;
      } else if (parsedMinZoom !== undefined && parsedMaxZoom < parsedMinZoom) {
        setMaxZoomError("Max zoom must be greater than min zoom");
        return;
      }

      const zoom = {
        ...(parsedMinZoom !== undefined && { min: parsedMinZoom }),
        ...(parsedMaxZoom !== undefined && { max: parsedMaxZoom }),
      };

      const updatedCamera: ThreeDPanelConfig["camera"] = {
        type: "orbit",
        target,
        zoom,
      };

      if (!lodash.isEqual(camera, updatedCamera)) {
        dispatch(
          setThreeDPanelConfig(panelId, {
            camera: updatedCamera,
          }),
        );
      }
    },
    [target, minZoom, maxZoom, entities, dispatch, panelId, camera],
  );

  const options: { id: string; mesh: "mesh" | "path" }[] = entityIds.flatMap(
    (id) => [
      ...(entities[id].mesh ? [{ id, mesh: "mesh" as const }] : []),
      ...(entities[id].path ? [{ id, mesh: "path" as const }] : []),
    ],
  );

  return (
    <div className={styles.container}>
      <Autocomplete
        freeSolo
        size="small"
        fullWidth
        options={options}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : `${option.id}.${option.mesh}`
        }
        isOptionEqualToValue={(option, value) =>
          option.id === value.id && option.mesh === value.mesh
        }
        onChange={(_, value) =>
          !!value && typeof value === "object" && setTarget(value)
        }
        value={target}
        className={`${styles.input} ${styles.field}`}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Target Entity ID"
            InputProps={{
              ...params.InputProps,
              className: styles.input,
            }}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true,
              className: styles.input,
            }}
            error={!!targetError}
            helperText={targetError}
          />
        )}
      />
      <div className={`${styles.inputContainer} ${styles.field}`}>
        <TextField
          label="Min Zoom"
          autoComplete="off"
          type="number"
          size="small"
          value={minZoom}
          onChange={(e) => setMinZoom(e.target.value)}
          error={!!minZoomError}
          helperText={minZoomError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: classNames(styles.input, styles.field) }}
        />
        <div className={styles.spacer} />
        <TextField
          label="Max Zoom"
          autoComplete="off"
          type="number"
          size="small"
          value={maxZoom}
          onChange={(e) => setMaxZoom(e.target.value)}
          error={!!maxZoomError}
          helperText={maxZoomError}
          className={styles.input}
          InputLabelProps={{ className: styles.input }}
          inputProps={{ className: classNames(styles.input, styles.field) }}
        />
      </div>
    </div>
  );
}
